import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { withSlash } from "@/lib/url";
import { detectLongWeekends, formatLongWeekendRange } from "@/lib/pseo";
import { getHolidaysForYear } from "@/data/holidays";
import { MONTH_SLUGS_ID, parseISO, todayISO } from "@/lib/calendar/dates";

const CURRENT = new Date().getFullYear();
const YEARS = [CURRENT, CURRENT + 1, CURRENT + 2, CURRENT + 3];

export const Route = createFileRoute("/harpitnas/")({
  head: () => {
    const title = "Harpitnas & Long Weekend Indonesia - Strategi Cuti Cerdas";
    const desc = "Daftar harpitnas (hari kejepit nasional) dan long weekend Indonesia per tahun. Rencanakan cuti minimal agar libur lebih panjang. Update otomatis sesuai libur nasional & cuti bersama.";
    const canonical = "https://hari.co.id/harpitnas/";
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: canonical },
        { name: "robots", content: "index, follow, max-image-preview:large" },
      ],
      links: [{ rel: "canonical", href: canonical }],
    };
  },
  component: HarpitnasIndex,
});

function HarpitnasIndex() {
  const todayIso = todayISO();
  // Build per-year stats and find next 5 upcoming harpitnas across years.
  const stats = YEARS.map((y) => {
    const all = detectLongWeekends(y, getHolidaysForYear(y));
    return {
      y,
      harpCount: all.filter((x) => x.category === "harpitnas").length,
      lwCount: all.filter((x) => x.category === "long-weekend").length,
      items: all,
    };
  });
  const upcoming = stats
    .flatMap((s) => s.items.map((x) => ({ ...x, y: s.y })))
    .filter((x) => x.startISO >= todayIso)
    .sort((a, b) => a.startISO.localeCompare(b.startISO))
    .slice(0, 5);

  return (
    <SiteShell>
      <div className="mx-auto max-w-4xl py-10">
        <nav className="mb-4 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-primary">Beranda</Link> / Harpitnas
        </nav>
        <h1 className="font-display text-4xl tracking-tight md:text-5xl">
          Harpitnas & Long Weekend Indonesia
        </h1>
        <p className="mt-3 max-w-3xl text-muted-foreground">
          <strong>Harpitnas</strong> (Hari Kejepit Nasional) adalah hari kerja yang
          terjepit di antara dua hari libur. Cukup ambil 1 hari cuti — libur Anda jadi
          3–4 hari beruntun. Halaman ini merangkum harpitnas & long weekend Indonesia
          dari libur nasional dan cuti bersama yang ditetapkan pemerintah.
        </p>

        {/* Upcoming */}
        {upcoming.length > 0 && (
          <section className="mt-8 rounded-xl border border-primary/30 bg-primary/5 p-5">
            <h2 className="text-xl font-semibold">Harpitnas & Long Weekend Mendatang Terdekat</h2>
            <ul className="mt-3 divide-y divide-border">
              {upcoming.map((x) => {
                const a = parseISO(x.startISO)!;
                return (
                  <li key={`${x.y}-${x.startISO}`} className="flex items-center justify-between py-2 text-sm">
                    <Link
                      to={withSlash(`/harpitnas/${x.y}/${MONTH_SLUGS_ID[a.m - 1]}`) as never}
                      className="font-medium hover:text-primary"
                    >
                      {formatLongWeekendRange(x)}
                    </Link>
                    <span className="text-xs text-muted-foreground">
                      {x.totalDays} hari · {x.category === "harpitnas" ? "Harpitnas" : "Long weekend"}
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {/* Per year cards */}
        <section className="mt-8">
          <h2 className="text-xl font-semibold">Pilih Tahun</h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {stats.map((s) => (
              <Link
                key={s.y}
                to={withSlash(`/harpitnas/${s.y}`) as never}
                className="rounded-lg border border-border bg-card p-4 hover:border-primary"
              >
                <div className="text-2xl font-semibold">Harpitnas {s.y}</div>
                <div className="mt-1 text-sm text-muted-foreground">
                  {s.harpCount} harpitnas · {s.lwCount} long weekend
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Strategy + about */}
        <section className="mt-10 grid gap-4 md:grid-cols-2">
          <article className="rounded-lg border border-border bg-card p-5">
            <h2 className="text-lg font-semibold">Strategi Cuti Cerdas</h2>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
              <li>Ambil cuti tepat di hari harpitnas — dapat libur 3–4 hari beruntun.</li>
              <li>Gabungkan harpitnas dengan cuti bersama untuk libur 5+ hari.</li>
              <li>Cek <Link to={withSlash(`/cuti-bersama/${CURRENT}`) as never} className="text-primary hover:underline">cuti bersama {CURRENT}</Link> agar tidak bentrok dengan jadwal kerja.</li>
              <li>Booking transportasi & hotel jauh hari karena harga melonjak saat long weekend.</li>
            </ul>
          </article>
          <article className="rounded-lg border border-border bg-card p-5">
            <h2 className="text-lg font-semibold">Apa Bedanya Harpitnas & Long Weekend?</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              <strong>Long weekend</strong> terbentuk otomatis saat libur nasional jatuh
              di Jumat/Senin → minimal 3 hari libur tanpa cuti. <strong>Harpitnas</strong>
              membutuhkan 1 hari cuti tambahan karena ada hari kerja terjepit di tengah.
              Lihat juga: <Link to={withSlash(`/libur-nasional/${CURRENT}`) as never} className="text-primary hover:underline">Libur Nasional {CURRENT}</Link>,
              {" "}<Link to={withSlash(`/tanggal-merah/${CURRENT}`) as never} className="text-primary hover:underline">Tanggal Merah {CURRENT}</Link>,
              {" "}<Link to={withSlash(`/kalender/${CURRENT}`) as never} className="text-primary hover:underline">Kalender {CURRENT}</Link>.
            </p>
          </article>
        </section>

        <section className="mt-8 rounded-lg border border-border bg-card p-5">
          <h2 className="text-xl font-semibold">FAQ Harpitnas</h2>
          <dl className="mt-3 space-y-3 text-sm">
            <div><dt className="font-medium">Apa itu harpitnas?</dt>
              <dd className="mt-1 text-muted-foreground">Hari kerja terjepit di antara dua hari libur. Cukup ambil 1 hari cuti agar libur jadi 3–4 hari beruntun.</dd></div>
            <div><dt className="font-medium">Berapa harpitnas tahun {CURRENT}?</dt>
              <dd className="mt-1 text-muted-foreground">Tahun {CURRENT} memiliki {stats[0]?.harpCount ?? 0} harpitnas dan {stats[0]?.lwCount ?? 0} long weekend — buka <Link to={withSlash(`/harpitnas/${CURRENT}`) as never} className="text-primary hover:underline">Harpitnas {CURRENT}</Link>.</dd></div>
            <div><dt className="font-medium">Apakah perusahaan wajib memberi cuti pada harpitnas?</dt>
              <dd className="mt-1 text-muted-foreground">Tidak wajib — harpitnas bukan libur resmi. Tapi banyak kantor menerapkan cuti bersama, atau karyawan mengajukan cuti pribadi pada hari tersebut.</dd></div>
          </dl>
        </section>
      </div>
    </SiteShell>
  );
}